import { EditOutlined, EyeFilled } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { Button, Tooltip, Typography } from 'antd';
import moment from 'moment';
import { ArrowClockwise, ChatText, PencilSimpleLine } from 'phosphor-react';
import React, { useCallback, useMemo, useState } from 'react';
import podcastPlaceholder from '../../assets/images/imagePlaceholder.png';
import {
  CLOUD_FRONT_CACHE_KEYS,
  LIST_TYPES,
  MODULES,
  PAGE_TYPES,
  PLAN_TYPE,
  ROUTES,
  WORKSPACE_ROLE_LEVEL,
  WORKSPACE_ROLE_PERMISSION
} from '../../common/constants';
import { getImageUrl } from '../../common/utils';
import FilterBar, { useFilterBar } from '../../components/FilterBar';
import Image from '../../components/Image';
import PageHeader from '../../components/PageHeader';
import PageList from '../../components/PageList';
import useCheckPermission from '../../hooks/useCheckPermission';
import AssignPlanModal from '../component/AssignPlanModal';
import CustomCard from '../component/card/Cscard';
import { INVALIDATE_CLOUD_FRONT_CACHE } from '../menus/graphql/Mutations';
import { GET_SUBSCRIPTION_PLANS } from '../subscriptions-plans/graphql/Queries';
import { Select, useModal } from '../videos/components/FormInputs';
import QuestionModal from './components/QuestionModal';
import { BULK_EDIT_PODCASTS } from './graphql/Mutations';
import { GET_PODCASTS_MODULE } from './graphql/Queries';

const PodcastCard = ({
  createdAt,
  id,
  title,
  description,
  imageThumbnail,
  onEdit,
  onPreview,
  isViewOnly,
  isAssetAllowed,
  isLabelAllowed
}) => {
  const [open, setOpen] = useState(false);
  const handleQuestion = () => {
    setOpen(true);
  };

  return (
    <>
      {open && <QuestionModal open={open} setOpen={setOpen} questionId={id} />}
      <CustomCard
        onClick={onPreview}
        alt={title}
        src={getImageUrl(imageThumbnail?.url) ?? podcastPlaceholder}
        blurHash={imageThumbnail?.blurhash}
        title={moment(createdAt).format('ll')}
        heading={title}
        description={description}
        primaryOnClick={() => onEdit(id)}
        primary={
          isAssetAllowed &&
          isLabelAllowed &&
          (isViewOnly ? (
            <>
              <EyeFilled size={16} />
              View
            </>
          ) : (
            <>
              <PencilSimpleLine size={16} />
              Edit
            </>
          ))
        }
        handleQuestion={handleQuestion}
        instanceType={PAGE_TYPES.PODCAST}
      />
    </>
  );
};

function ViewQuestionAction({ id }) {
  const [open, setOpen] = useState(false);
  const handleQuestion = () => {
    setOpen(true);
  };

  return (
    <>
      {open && <QuestionModal open={open} setOpen={setOpen} questionId={id} />}
      <div className="d-flex align-center">
        <Tooltip title="Manage Question">
          <Button type="text" className="text-btn" onClick={handleQuestion}>
            <ChatText width={24} height={24} />
          </Button>
        </Tooltip>
      </div>
    </>
  );
}

const getColumns = ({
  handleEdit: onEdit,
  isAssetAllowed,
  isLabelAllowed,
  isViewOnly
}) => [
  {
    title: 'Video',
    dataIndex: 'imageThumbnail',
    ellipsis: true,
    render: (value, { title }) => (
      <Image
        className="group-img-table"
        blurHash={value?.blurhash}
        src={
          getImageUrl(value?.url, { height: 50, width: 80 }) ??
          podcastPlaceholder
        }
        alt={title}
      />
    )
  },
  {
    title: 'Title',
    dataIndex: 'title',
    ellipsis: true
  },
  {
    title: 'Description',
    dataIndex: 'description',
    ellipsis: true
  },
  ...(isAssetAllowed && isLabelAllowed
    ? [
        {
          title: 'Action',
          dataIndex: 'id',
          render: (value) => (
            <div className="d-flex align-center">
              <ViewQuestionAction id={value} />
              <Tooltip title={`${isViewOnly ? 'View' : 'Edit'} Podcast`}>
                <Button
                  type="text"
                  className="text-btn"
                  onClick={() => onEdit(value)}
                >
                  {isViewOnly ? <EyeFilled /> : <EditOutlined />}
                </Button>
              </Tooltip>
            </div>
          )
        }
      ]
    : [])
];

const variablesSelector = ({
  limit,
  offset,
  search,
  sortField,
  sortOrder
}) => ({
  filter: {
    skip: offset,
    search,
    limit
  },
  sort: {
    sortOn: sortField,
    sortBy: sortOrder
  }
});

const dataSelector = ({ podcastsAdmin }) => ({
  data: podcastsAdmin?.podcasts ?? [],
  count: podcastsAdmin?.count ?? 0
});

const Podcast = ({ history }) => {
  const filterProps = useFilterBar();
  const { open, openModal, closeModal } = useModal();

  const [isBulkEdit, setIsBulkEdit] = useState(false);
  const [selectedPodcastIds, setSelectedPodcastIds] = useState([]);
  const [selectedPlanKey, setSelectedPlanKey] = useState([]);

  const [invalidateCache, { loading: isInvalidating }] = useMutation(
    INVALIDATE_CLOUD_FRONT_CACHE,
    {
      variables: { key: CLOUD_FRONT_CACHE_KEYS.OG_IMAGES }
    }
  );

  const [bulkEditPodcastMutatuion, { loading: submitLoading }] = useMutation(
    BULK_EDIT_PODCASTS,
    {
      onError() {},
      onCompleted() {
        setSelectedPlanKey([]);
        setSelectedPodcastIds([]);
        closeModal();
      }
    }
  );

  const isAddEditAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.CONTENT_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const isLabelAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.LABEL_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.VIEW,
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const isViewOnly = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.CONTENT_MANAGEMENT,
      allowedPermissions: [WORKSPACE_ROLE_LEVEL.VIEW]
    }
  ]);

  const isAssetAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.ASSET_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.VIEW,
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const handleShowModal = () => {
    history.push(`${ROUTES?.PODCAST_MODULE}/add`);
  };

  const handleEdit = useCallback((id) => {
    history.push(`${ROUTES?.PODCAST_MODULE}/${id}/edit`);
  }, []);

  const columns = useMemo(
    () =>
      getColumns({
        handleEdit,
        isAddEditAllowed,
        isAssetAllowed,
        isLabelAllowed,
        isViewOnly
      }),
    [handleEdit]
  );

  const handleBulkEdit = () => {
    setIsBulkEdit(true);
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedPodcastIds(newSelectedRowKeys);
  };

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedPodcastIds,
      onChange: onSelectChange
    }),
    [selectedPodcastIds]
  );

  const handlePlanSelection = (plans) => {
    const arrayOfkeys = plans?.map((plan) => plan?.key);
    setSelectedPodcastIds([]);
    setSelectedPlanKey(arrayOfkeys);
  };

  const queryFilters = useMemo(() => {
    return {
      ...filterProps.filters,
      ...(isBulkEdit &&
        selectedPlanKey.length > 0 && {
          allowedPlans: selectedPlanKey
        })
    };
  }, [filterProps.filters, isBulkEdit, selectedPlanKey]);

  return (
    <div>
      <AssignPlanModal
        open={open}
        onClose={closeModal}
        selectedPlanKey={selectedPlanKey}
        selectedIds={selectedPodcastIds}
        bulkEditMutatuion={bulkEditPodcastMutatuion}
        submitLoading={submitLoading}
      />
      <PageHeader
        menu={MODULES?.CONTENT_UNITS}
        handleShowModal={handleShowModal}
        isAddEditAllowed={isAddEditAllowed && isAssetAllowed && isLabelAllowed}
        isBulkEditAllowed
        bulkEditDisabled={
          isBulkEdit &&
          selectedPlanKey?.length <= 0 &&
          selectedPodcastIds?.length <= 0
        }
        isBulkEdit={isBulkEdit}
        handleBulkEdit={handleBulkEdit}
        openModal={openModal}
        selectedPlanKey={selectedPlanKey}
        selectedIds={selectedPodcastIds}
      />
      <FilterBar
        {...filterProps}
        {...(isBulkEdit && {
          show: {
            sort: false,
            listModes: false
          }
        })}
        SearchProps={{ placeholder: 'Search Videos' }}
        extraFilters={
          isBulkEdit && (
            <>
              <Typography.Text className="sort-by-title" type="secondary">
                Select Plan
              </Typography.Text>
              <Select
                multiple
                popupMatchSelectWidth={false}
                excludeOptions={[PLAN_TYPE.GUEST, PLAN_TYPE.FREE]}
                placeholder="Select plan"
                query={GET_SUBSCRIPTION_PLANS}
                variablesSelector={(filter) => ({
                  filter: {
                    isActive: true,
                    ...filter
                  }
                })}
                allowClear
                dataSelector={(res) =>
                  res?.subscriptionPlans?.subscriptionPlans?.map(({ key }) => ({
                    label: key,
                    value: key
                  })) ?? {}
                }
                keys={{
                  data: 'subscriptionPlans',
                  records: 'subscriptionPlans',
                  count: 'count'
                }}
                onChange={handlePlanSelection}
                maxTagCount="responsive"
                className="plan-selection"
              />
            </>
          )
        }
      >
        <Tooltip title="Revalidate OG images" placement="left">
          <Button
            className="text-btn d-flex align-center justify-center"
            type="text"
            icon={<ArrowClockwise size={24} />}
            onClick={invalidateCache}
            loading={isInvalidating}
          />
        </Tooltip>
      </FilterBar>
      <PageList
        filters={queryFilters}
        listMode={isBulkEdit ? LIST_TYPES.TABLE : filterProps.listMode}
        query={GET_PODCASTS_MODULE}
        variablesSelector={variablesSelector}
        dataSelector={dataSelector}
        GridProps={{
          renderItem: (props) => (
            <PodcastCard
              {...props}
              onEdit={handleEdit}
              isAddEditAllowed={isAddEditAllowed}
              isAssetAllowed={isAssetAllowed}
              isLabelAllowed={isLabelAllowed}
              isViewOnly={isViewOnly}
            />
          )
        }}
        TableProps={{
          columns,
          ...(isBulkEdit && selectedPlanKey?.length <= 0 && { rowSelection })
        }}
      />
    </div>
  );
};

export default Podcast;
